/**
 * API Service for Frontend
 * Function-based HTTP client with comprehensive error handling and notifications
 */

import {
	handleMutationNotification,
	handleErrorNotification,
} from "./notification.service";

/**
 * Standard API Response Interface matching backend response format
 */
export interface ApiResponse<T = any> {
	success: boolean;
	data?: T;
	error?: string;
	message?: string;
	count?: number;
	timestamp?: string;
	meta?: {
		page?: number;
		limit?: number;
		total?: number;
		totalPages?: number;
	};
}

/**
 * Request configuration options
 */
export interface RequestConfig extends RequestInit {
	params?: Record<string, any>;
	timeout?: number;
	notification?: {
		enabled?: boolean;
		showSuccess?: boolean;
		showError?: boolean;
		successMessage?: string;
		errorMessage?: string;
	};
}

/**
 * API Error class for better error handling
 */
export class ApiError extends Error {
	public status: number;
	public data: any;

	constructor(message: string, status: number, data?: any) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.data = data;
	}
}

// Configuration - use Vite's import.meta.env directly
// Vite will replace these at build time based on the mode
const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api";
const DEFAULT_TIMEOUT = 30000; // 30 seconds

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get authentication headers
 */
function getAuthHeaders(): HeadersInit {
	const token = localStorage.getItem("accessToken");
	const headers: HeadersInit = {
		"Content-Type": "application/json",
		Accept: "application/json",
		"Cache-Control": "no-cache",
		Pragma: "no-cache",
	};

	if (token) {
		headers["Authorization"] = `Bearer ${token}`;
	}

	return headers;
}

/**
 * Build URL with query parameters
 */
function buildURL(endpoint: string, params?: Record<string, any>): string {
	const url = endpoint.startsWith("http") ? endpoint : `${BASE_URL}${endpoint}`;

	if (!params || Object.keys(params).length === 0) {
		return url;
	}

	const queryString = Object.entries(params)
		.filter(([_, value]) => value !== undefined && value !== null)
		.map(
			([key, value]) =>
				`${encodeURIComponent(key)}=${encodeURIComponent(value)}`
		)
		.join("&");

	return queryString ? `${url}?${queryString}` : url;
}

/**
 * Handle response and errors
 */
async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
	const contentType = response.headers.get("content-type");

	if (!response.ok) {
		let errorData: any = {};

		if (contentType?.includes("application/json")) {
			errorData = await response.json();
		} else {
			errorData = { message: await response.text() };
		}

		throw new ApiError(
			errorData.error || errorData.message || "Request failed",
			response.status,
			errorData
		);
	}

	if (response.status === 204) {
		return { success: true } as ApiResponse<T>;
	}

	if (contentType?.includes("application/json")) {
		return await response.json();
	}

	const text = await response.text();
	return { success: true, data: text as any };
}

/**
 * Create request with timeout
 */
function createRequestWithTimeout(
	url: string,
	config: RequestConfig
): Promise<Response> {
	const timeout = config.timeout || DEFAULT_TIMEOUT;
	const controller = new AbortController();

	const timeoutId = setTimeout(() => controller.abort(), timeout);

	return fetch(url, {
		...config,
		signal: controller.signal,
	}).finally(() => clearTimeout(timeoutId));
}

/**
 * Handle token refresh for unauthorized requests
 */
async function handleUnauthorized(): Promise<boolean> {
	const refreshToken = localStorage.getItem("refreshToken");
	if (!refreshToken) {
		// No refresh token, logout
		localStorage.removeItem("accessToken");
		localStorage.removeItem("refreshToken");
		localStorage.removeItem("currentUser");
		window.location.href = "/";
		return false;
	}

	try {
		// Manually refresh token using fetch to avoid circular dependency with auth hooks
		const response = await fetch(`${BASE_URL}/auth/refresh`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken }),
		});

		if (response.ok) {
			const result = await response.json();
			// Backend wraps response in 'data' field via responseService.success()
			const data = result.data || result;

			if (data.accessToken && data.refreshToken) {
				localStorage.setItem("accessToken", data.accessToken);
				localStorage.setItem("refreshToken", data.refreshToken);
				return true;
			} else {
				console.error("Invalid refresh response format:", result);
				return false;
			}
		}
	} catch (error) {
		console.error("Token refresh failed:", error);
	}

	// If refresh failed, logout
	localStorage.removeItem("accessToken");
	localStorage.removeItem("refreshToken");
	localStorage.removeItem("currentUser");
	window.location.href = "/";
	return false;
}

// ============================================================================
// CORE REQUEST FUNCTION
// ============================================================================

/**
 * Generic request function with full feature support
 */
async function request<T = any>(
	method: string,
	endpoint: string,
	data?: any,
	config?: RequestConfig
): Promise<ApiResponse<T>> {
	const url = buildURL(endpoint, config?.params);

	// Don't use auth headers for public auth endpoints (login, register, check-email)
	// But DO use auth headers for protected endpoints like /auth/me
	const isPublicAuthEndpoint =
		endpoint.includes("/auth/login") ||
		endpoint.includes("/auth/register") ||
		endpoint.includes("/auth/check-email") ||
		endpoint.includes("/auth/google") ||
		endpoint.includes("/auth/refresh") ||
		endpoint.includes("/auth/forgot-password") ||
		endpoint.includes("/auth/reset-password") ||
		endpoint.includes("/auth/validate-reset-token") ||
		endpoint.includes("/verification/email") ||
		endpoint.includes("/verification/phone") ||
		endpoint.includes("/invitations/validate");
	const useAuthHeaders = !isPublicAuthEndpoint;

	const requestConfig: RequestConfig = {
		method,
		headers: {
			...(useAuthHeaders
				? getAuthHeaders()
				: {
						"Content-Type": "application/json",
						"Cache-Control": "no-cache",
						Pragma: "no-cache",
					}),
			...config?.headers,
		},
		...config,
	};

	if (data && ["POST", "PUT", "PATCH"].includes(method)) {
		requestConfig.body = JSON.stringify(data);
	}

	// Setup notification handlers for mutations (POST, PUT, PATCH, DELETE)
	const isMutation = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
	const notificationEnabled = config?.notification?.enabled !== false;

	let notificationHandlers: ReturnType<
		typeof handleMutationNotification
	> | null = null;
	if (isMutation && notificationEnabled) {
		notificationHandlers = handleMutationNotification(
			method as "POST" | "PUT" | "PATCH" | "DELETE",
			endpoint,
			config?.notification
		);
	}

	try {
		const response = await createRequestWithTimeout(url, requestConfig);

		// Handle 401 unauthorized with token refresh
		if (response.status === 401 && useAuthHeaders) {
			const refreshed = await handleUnauthorized();
			if (refreshed) {
				// Retry the request with new token
				return request<T>(method, endpoint, data, config);
			}
			throw new ApiError("Unauthorized", 401);
		}

		const result = await handleResponse<T>(response);

		// Show success notification for mutations
		if (isMutation && notificationEnabled && result.success) {
			notificationHandlers?.onSuccess(result);
		}

		// If the response indicates an error (success: false), show error notification
		if (!result.success && (result.error || result.message)) {
			handleErrorNotification(
				method,
				endpoint,
				result, // Pass the full result object to get both error and message
				config?.notification
			);
		}

		return result;
	} catch (error) {
		// Handle error notifications
		let errorMessage = "An unexpected error occurred";

		if (error instanceof ApiError) {
			// Pass the error data if available, otherwise just the message
			handleErrorNotification(
				method,
				endpoint,
				error.data || error.message,
				config?.notification
			);
			throw error;
		}

		if (error instanceof Error) {
			if (error.name === "AbortError") {
				errorMessage = "Request timeout - please try again";
				handleErrorNotification(
					method,
					endpoint,
					errorMessage,
					config?.notification
				);
				throw new ApiError(errorMessage, 408);
			}
			errorMessage = error.message;
			handleErrorNotification(
				method,
				endpoint,
				errorMessage,
				config?.notification
			);
			throw new ApiError(errorMessage, 0);
		}

		handleErrorNotification(
			method,
			endpoint,
			errorMessage,
			config?.notification
		);
		throw new ApiError(errorMessage, 0);
	}
}

// ============================================================================
// HTTP METHOD FUNCTIONS
// ============================================================================

/**
 * GET request
 */
export async function apiGet<T = any>(
	endpoint: string,
	params?: Record<string, any>,
	config?: RequestConfig
): Promise<ApiResponse<T>> {
	return request<T>("GET", endpoint, null, { ...config, params });
}

/**
 * POST request
 */
export async function apiPost<T = any>(
	endpoint: string,
	data?: any,
	config?: RequestConfig
): Promise<ApiResponse<T>> {
	return request<T>("POST", endpoint, data, config);
}

/**
 * PUT request
 */
export async function apiPut<T = any>(
	endpoint: string,
	data?: any,
	config?: RequestConfig
): Promise<ApiResponse<T>> {
	return request<T>("PUT", endpoint, data, config);
}

/**
 * PATCH request
 */
export async function apiPatch<T = any>(
	endpoint: string,
	data?: any,
	config?: RequestConfig
): Promise<ApiResponse<T>> {
	return request<T>("PATCH", endpoint, data, config);
}

/**
 * DELETE request
 */
export async function apiDelete<T = any>(
	endpoint: string,
	config?: RequestConfig
): Promise<ApiResponse<T>> {
	return request<T>("DELETE", endpoint, null, config);
}

// ============================================================================
// SPECIALIZED FUNCTIONS
// ============================================================================

/**
 * Upload file
 */
export async function apiUpload<T = any>(
	endpoint: string,
	formData: FormData,
	config?: RequestConfig
): Promise<ApiResponse<T>> {
	const url = buildURL(endpoint, config?.params);

	const headers = { ...getAuthHeaders() };
	delete (headers as any)["Content-Type"]; // Let browser set content-type for FormData

	const requestConfig: RequestConfig = {
		method: "POST",
		headers: {
			...headers,
			...config?.headers,
		},
		body: formData,
		...config,
	};

	try {
		const response = await createRequestWithTimeout(url, requestConfig);
		return await handleResponse<T>(response);
	} catch (error) {
		if (error instanceof ApiError) {
			throw error;
		}

		if (error instanceof Error) {
			throw new ApiError(error.message, 0);
		}

		throw new ApiError("Upload failed", 0);
	}
}

/**
 * Download file
 */
export async function apiDownload(
	endpoint: string,
	filename?: string,
	config?: RequestConfig
): Promise<void> {
	const url = buildURL(endpoint, config?.params);

	const requestConfig: RequestConfig = {
		method: "GET",
		headers: {
			...getAuthHeaders(),
			...config?.headers,
		},
		...config,
	};

	try {
		const response = await createRequestWithTimeout(url, requestConfig);

		if (!response.ok) {
			const errorData = await response.json();
			throw new ApiError(
				errorData.error || "Download failed",
				response.status,
				errorData
			);
		}

		const blob = await response.blob();
		const downloadUrl = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = downloadUrl;
		link.download = filename || "download";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);
	} catch (error) {
		if (error instanceof ApiError) {
			throw error;
		}

		if (error instanceof Error) {
			throw new ApiError(error.message, 0);
		}

		throw new ApiError("Download failed", 0);
	}
}

/**
 * Batch requests
 */
export async function apiBatch<T = any>(
	requests: Array<{
		method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
		endpoint: string;
		data?: any;
		params?: Record<string, any>;
		config?: RequestConfig;
	}>
): Promise<ApiResponse<T>[]> {
	const promises = requests.map((req) => {
		const config = { ...req.config, params: req.params };

		switch (req.method) {
			case "GET":
				return apiGet(req.endpoint, req.params, req.config);
			case "POST":
				return apiPost(req.endpoint, req.data, config);
			case "PUT":
				return apiPut(req.endpoint, req.data, config);
			case "PATCH":
				return apiPatch(req.endpoint, req.data, config);
			case "DELETE":
				return apiDelete(req.endpoint, config);
			default:
				throw new Error(`Unsupported method: ${req.method}`);
		}
	});

	return Promise.all(promises);
}

// ============================================================================
// LEGACY CLASS-BASED API SERVICE (for backward compatibility)
// ============================================================================

/**
 * Legacy class-based API service
 * @deprecated Use function-based API methods instead
 */
class ApiService {
	async get<T = any>(
		endpoint: string,
		params?: Record<string, any>,
		config?: RequestConfig
	): Promise<ApiResponse<T>> {
		return apiGet<T>(endpoint, params, config);
	}

	async post<T = any>(
		endpoint: string,
		data?: any,
		config?: RequestConfig
	): Promise<ApiResponse<T>> {
		return apiPost<T>(endpoint, data, config);
	}

	async put<T = any>(
		endpoint: string,
		data?: any,
		config?: RequestConfig
	): Promise<ApiResponse<T>> {
		return apiPut<T>(endpoint, data, config);
	}

	async patch<T = any>(
		endpoint: string,
		data?: any,
		config?: RequestConfig
	): Promise<ApiResponse<T>> {
		return apiPatch<T>(endpoint, data, config);
	}

	async delete<T = any>(
		endpoint: string,
		config?: RequestConfig
	): Promise<ApiResponse<T>> {
		return apiDelete<T>(endpoint, config);
	}

	async upload<T = any>(
		endpoint: string,
		formData: FormData,
		config?: RequestConfig
	): Promise<ApiResponse<T>> {
		return apiUpload<T>(endpoint, formData, config);
	}

	async download(
		endpoint: string,
		filename?: string,
		config?: RequestConfig
	): Promise<void> {
		return apiDownload(endpoint, filename, config);
	}

	async batch<T = any>(
		requests: Array<{
			method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
			endpoint: string;
			data?: any;
			params?: Record<string, any>;
		}>
	): Promise<ApiResponse<T>[]> {
		return apiBatch<T>(requests);
	}
}

// Export singleton instance for backward compatibility
const apiService = new ApiService();
export default apiService;

// Export class for testing or multiple instances
export { ApiService };
